import jsPDF from 'jspdf';
import { QuoteWithDetails, QuoteCondition } from '../types/product';
import { quoteService } from './quoteService';
import { productService } from './productService';

// Interfaz para las opciones de justificación de texto
interface JustificationOptions {
  fontSize?: number;
  lineHeight?: number;
  justifyLastLine?: boolean;
  minSpacing?: number;
}

// Interfaz para el resultado de funciones con saltos de página
interface PageBreakResult {
  finalY: number;
  pagesAdded: number;
}

// Interfaz para opciones de salto de página
interface PageBreakOptions {
  minSpace?: number; // Espacio mínimo requerido antes del salto
  repeatHeader?: boolean; // Si se debe repetir el encabezado
  headerFunction?: (pdf: jsPDF, y: number) => number; // Función personalizada para repetir encabezado
}

// Interfaz para el resultado de funciones en columnas
interface ColumnResult {
  finalY: number;
  usedColumns: number;
}

// Utilidades de justificación de texto exportables para uso en otros documentos
export const textJustificationUtils = {
  /**
   * Calcula el espaciado óptimo entre palabras para justificación de texto
   * @param words - Array de palabras a justificar
   * @param maxWidth - Ancho máximo disponible para el texto
   * @param pdf - Instancia de jsPDF
   * @returns Objeto con el espaciado entre palabras y los anchos individuales
   */
  calculateOptimalSpacing: (
    words: string[],
    maxWidth: number,
    pdf: jsPDF
  ): { spaceBetweenWords: number; wordWidths: number[] } => {
    const wordWidths: number[] = [];
    let totalTextWidth = 0;
    
    // Calcular el ancho de cada palabra y el total
    words.forEach(word => {
      const width = pdf.getTextWidth(word);
      wordWidths.push(width);
      totalTextWidth += width;
    });
    
    // Calcular el espacio disponible entre palabras
    const totalSpaceAvailable = maxWidth - totalTextWidth;
    const spaceBetweenWords = words.length > 1 ? totalSpaceAvailable / (words.length - 1) : 0;
    
    return { spaceBetweenWords, wordWidths };
  },

  /**
   * Ajusta el espaciado para textos con caracteres especiales o acentos
   * @param text - Texto a analizar
   * @param spacing - Espaciado original
   * @returns Espaciado ajustado
   */
  adjustForSpecialChars: (text: string, spacing: number): number => {
    // Reducir espaciado para textos con muchos caracteres especiales o acentos
    const specialCharCount = (text.match(/[áéíóúÁÉÍÓÚñÑüÜ]/g) || []).length;
    const charCount = text.length;
    const specialCharRatio = specialCharCount / charCount;
    
    if (specialCharRatio > 0.1) {
      return spacing * 0.95; // Reducir 5% si hay más del 10% de caracteres especiales
    }
    
    return spacing;
  },

  /**
   * Función principal de justificación mejorada con múltiples opciones
   * @param pdf - Instancia de jsPDF
   * @param text - Texto a justificar
   * @param x - Posición X inicial
   * @param y - Posición Y inicial
   * @param maxWidth - Ancho máximo disponible
   * @param options - Opciones de justificación
   * @returns Posición Y final después de agregar el texto
   */
  addEnhancedJustifiedText: (
    pdf: jsPDF,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    options: JustificationOptions = {}
  ): number => {
    const {
      fontSize = 9,
      lineHeight = 0.5,
      justifyLastLine = false,
      minSpacing = 1.0
    } = options;
    
    // Guardar configuración actual
    const currentFontSize = pdf.getFontSize();
    const currentFont = pdf.getFont();
    
    // Aplicar configuración temporal
    pdf.setFontSize(fontSize);
    
    const words = text.split(' ');
    let currentY = y;
    const lines: string[] = [];
    let currentLine = '';
    
    // Dividir texto en líneas
    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const textWidth = pdf.getTextWidth(testLine);
      
      if (textWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    // Procesar cada línea
    lines.forEach((lineText, lineIndex) => {
      const isLastLine = lineIndex === lines.length - 1;
      const wordsInLine = lineText.split(' ');
      
      // Decidir si justificar esta línea
      const shouldJustify = !isLastLine || (justifyLastLine && wordsInLine.length > 1);
      
      if (!shouldJustify || wordsInLine.length <= 1) {
        // No justificar: alinear a la izquierda
        pdf.text(lineText, x, currentY);
      } else {
        // Justificar línea
        const { spaceBetweenWords, wordWidths } = textJustificationUtils.calculateOptimalSpacing(wordsInLine, maxWidth, pdf);
        let adjustedSpacing = textJustificationUtils.adjustForSpecialChars(lineText, spaceBetweenWords);
        
        // Asegurar espaciado mínimo
        adjustedSpacing = Math.max(adjustedSpacing, minSpacing);
        
        // Dibujar palabras con espaciado calculado
        let currentX = x;
        wordsInLine.forEach((word, wordIndex) => {
          pdf.text(word, currentX, currentY);
          currentX += wordWidths[wordIndex];
          
          // Agregar espacio entre palabras (excepto después de la última)
          if (wordIndex < wordsInLine.length - 1) {
            const endsWithPunctuation = /[.,;:!?]$/.test(word);
            const finalSpacing = endsWithPunctuation ?
              adjustedSpacing * 0.7 :
              adjustedSpacing;
            currentX += finalSpacing;
          }
        });
      }
      
      // Avanzar a la siguiente línea
      currentY += fontSize * lineHeight;
    });
    
    // Restaurar configuración original
    pdf.setFontSize(currentFontSize);
    pdf.setFont(currentFont.fontName, currentFont.fontStyle);
    
    return currentY;
  }
};

// Función unificada para manejar saltos de página
const handlePageBreak = (
  pdf: jsPDF,
  currentY: number,
  requiredHeight: number,
  margins: { left: number; top: number; right: number; bottom: number },
  options: PageBreakOptions = {}
): { newY: number; pageAdded: boolean } => {
  const {
    minSpace = 10, // Espacio mínimo requerido antes del salto
    repeatHeader = false,
    headerFunction
  } = options;
  
  // Calcular el límite inferior real (297mm es la altura de A4)
  const maxY = 297 - margins.bottom - minSpace;
  
  // Verificar si necesitamos un salto de página
  if (currentY + requiredHeight > maxY) {
    pdf.addPage();
    
    // Determinar el margen superior según el número de página
    const currentPage = pdf.getNumberOfPages();
    const isFirstPage = currentPage === 1;
    const topMargin = isFirstPage ? margins.top : 20; // 2cm = 20mm para páginas siguientes
    
    let newY = topMargin;
    
    // Repetir encabezado si es necesario
    if (repeatHeader && headerFunction) {
      const headerResult = headerFunction(pdf, newY);
      newY = headerResult || newY;
    }
    
    return { newY, pageAdded: true };
  }
  
  return { newY: currentY, pageAdded: false };
};

// Función para verificar si hay espacio suficiente para contenido
const hasEnoughSpace = (
  currentY: number,
  requiredHeight: number,
  margins: { left: number; top: number; right: number; bottom: number },
  minSpace: number = 10
): boolean => {
  const maxY = 297 - margins.bottom - minSpace;
  return currentY + requiredHeight <= maxY;
};

// Función para agregar pie de página en todas las páginas
const addFooter = (pdf: jsPDF, margins: { left: number; top: number; right: number; bottom: number }) => {
  const pageCount = pdf.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    const footerY = 297 - 15; // 15mm desde el borde inferior
    pdf.setFontSize(8); // Tamaño de fuente 8
    pdf.setFont('helvetica', 'normal');
    
    // Información de contacto separada por líneas verticales
    const emailText = 'cotizaciones@atomcorporations.com';
    const phoneText = '+51 953 759 419';
    const webText = 'www.atomcorporations.com';
    const separator = '|';
    
    // Calcular anchos para centrar
    const emailWidth = pdf.getTextWidth(emailText);
    const phoneWidth = pdf.getTextWidth(phoneText);
    const webWidth = pdf.getTextWidth(webText);
    const separatorWidth = pdf.getTextWidth(separator);
    
    // Espaciado entre elementos
    const spacing = 8;
    const totalWidth = emailWidth + separatorWidth + phoneWidth + separatorWidth + webWidth + (spacing * 4);
    const startX = (210 - totalWidth) / 2;
    
    // Posicionar cada elemento
    let currentX = startX;
    pdf.text(emailText, currentX, footerY);
    currentX += emailWidth + spacing;
    pdf.text(separator, currentX, footerY);
    currentX += separatorWidth + spacing;
    pdf.text(phoneText, currentX, footerY);
    currentX += phoneWidth + spacing;
    pdf.text(separator, currentX, footerY);
    currentX += separatorWidth + spacing;
    pdf.text(webText, currentX, footerY);
  }
};

// Función para agregar texto línea por línea con verificación de salto de página
const addTextLineByLine = (
  pdf: jsPDF,
  lines: Array<{ text: string; fontSize?: number; fontStyle?: string }>,
  x: number,
  y: number,
  margins: { left: number; top: number; right: number; bottom: number },
  lineHeight: number = 6
): number => {
  let currentY = y;
  
  for (const line of lines) {
    // Verificar si necesitamos una nueva página antes de esta línea
    const pageBreakResult = handlePageBreak(
      pdf,
      currentY,
      lineHeight,
      margins,
      { minSpace: lineHeight }
    );
    currentY = pageBreakResult.newY;
    
    // Aplicar estilo de fuente si se especifica
    if (line.fontSize) pdf.setFontSize(line.fontSize);
    if (line.fontStyle) pdf.setFont('helvetica', line.fontStyle as any);
    
    // Agregar la línea
    pdf.text(line.text, x, currentY);
    currentY += lineHeight;
  }
  
  return currentY;
};

export const pdfService = {
  // Generar PDF de cotización con formato específico (usando el mismo formato que el correo)
  generateQuotePDF: async (quote: QuoteWithDetails, additionalConditions?: QuoteCondition[]): Promise<void> => {
    try {
      // Usar la misma función que genera el PDF para correo electrónico
      const pdfBase64 = await pdfService.generateQuotePDFBase64(quote, additionalConditions);
      
      if (!pdfBase64) {
        throw new Error('No se pudo generar el PDF de la cotización');
      }

      // Convertir el base64 a un blob y descargar
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Extraer el código de cotización del mensaje o generar uno nuevo
      const messageParts = quote.message.split(' - ');
      let quoteCode = messageParts[0] || quoteService.generateQuoteCode();
      
      // Si el código contiene la palabra "Cotización", eliminarla
      if (quoteCode.startsWith('Cotización ')) {
        quoteCode = quoteCode.replace('Cotización ', '');
      }
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quoteCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar PDF:', error);
      throw new Error('No se pudo generar el PDF de la cotización');
    }
  },

  // Generar PDF de cotización como base64 para enviar por correo
  generateQuotePDFBase64: async (quote: QuoteWithDetails, additionalConditions?: QuoteCondition[]): Promise<string | null> => {
    try {
      // Obtener los nombres de los productos
      const products = await productService.getProducts();
      
      // Extraer información del cliente
      const customerName = quote.company || quote.name;
      const attentionContact = quote.name;
      
      // Extraer el código de cotización del mensaje o generar uno nuevo
      const messageParts = quote.message.split(' - ');
      let quoteCode = messageParts[0] || quoteService.generateQuoteCode();
      
      // Si el código contiene la palabra "Cotización", eliminarla
      if (quoteCode.startsWith('Cotización ')) {
        quoteCode = quoteCode.replace('Cotización ', '');
      }
      
      const currentDate = new Date().toLocaleDateString('es-ES');
      
      // Usar la fecha de validez de la cotización si está disponible, sino calcular por defecto
      let validityDateStr = '';
      if (quote.validity_date) {
        const validityDate = new Date(quote.validity_date);
        validityDateStr = validityDate.toLocaleDateString('es-ES');
      } else {
        const validityDate = new Date();
        validityDate.setDate(validityDate.getDate() + 30);
        validityDateStr = validityDate.toLocaleDateString('es-ES');
      }
      
      // Calcular totales
      const subtotal = quote.quote_details?.reduce((sum: number, item: any) => sum + (item.total_price || 0), 0) || 0;
      
      // Obtener los términos de pago y entrega de la cotización, con valores por defecto
      // Usar los nuevos campos payment_type y credit_days si están disponibles
      let paymentTerms = quote.payment_terms || 'Crédito a 30 días';
      
      // Si tenemos los campos estructurados, usarlos para generar el texto
      if (quote.payment_type && quote.credit_days !== undefined) {
        if (quote.payment_type === 'contado') {
          paymentTerms = 'Contado';
        } else if (quote.payment_type === 'credito' && quote.credit_days > 0) {
          paymentTerms = `Crédito a ${quote.credit_days} días`;
        }
      }
      
      // Para el tiempo de entrega, usar el campo delivery_days si existe, sino el delivery_terms
      let deliveryTerms = '';
      if (quote.delivery_days !== undefined && quote.delivery_days > 0) {
        deliveryTerms = `${quote.delivery_days} días hábiles después de la orden de compra`;
      } else {
        deliveryTerms = quote.delivery_terms || '5 días hábiles después de la orden de compra';
      }
      
      const currency = quote.currency || 'USD';
      // Observación fija con el nombre de la empresa
      const fixedObservation = `Los precios unitarios son fijos por cada producto y no incluyen El Impuesto General a las Ventas (IGV). La cantidad final deberá ser confirmada por ${customerName}.`;
      
      // Observaciones adicionales del formulario (filtrar datos del formulario web)
      const additionalObservations = quote.message.split(' - ').slice(1).join(' - ') || '';
      
      // Filtrar datos del formulario web que no deben ir en el PDF
      const filteredObservations = additionalObservations
        .split('\n\n')
        .filter(line => {
          // Excluir líneas que contienen datos del formulario web
          const excludePatterns = [
            'Servicio de interés:',
            'Industria:',
            'RUC:',
            'Especificaciones:',
            'Urgencia:',
            'Mensaje de prueba adicional'
          ];
          return !excludePatterns.some(pattern => line.includes(pattern));
        })
        .join('\n\n');
      
      // Crear PDF directamente con jsPDF con tamaño A4 y márgenes correctos
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });
      
      // Definir márgenes (izquierdo, superior, derecho, inferior) en mm
      const margins = {
        left: 30,    // 3cm izquierdo
        top: 15,     // 1.5cm superior
        right: 30,   // 3cm derecho
        bottom: 20    // 2cm inferior
      };
      
      // Ancho útil del área de contenido
      const contentWidth = 210 - margins.left - margins.right;
      
      // Configurar fuentes - tamaño más pequeño
      pdf.setFont('helvetica');
      pdf.setFontSize(9);
      
      let yPosition = margins.top; // Posición Y inicial con margen superior
      
      // Agregar logo más grande (20% más grande) y bien posicionado en esquina superior derecha
      try {
        // Intentar cargar el logo desde la ruta pública
        const logoUrl = window.location.origin + '/logo.png';
        // Logo más grande (48x14.4mm) - 20% más grande que antes
        // Posicionado en esquina superior derecha sin pasar margen derecho
        const logoWidth = 48;
        const logoHeight = 14.4;
        const logoX = 215 - margins.right - logoWidth; // Posición X para que esté alineado al margen derecho
        pdf.addImage(logoUrl, 'PNG', logoX, margins.top, logoWidth, logoHeight);
        
        // Ajustar la posición Y inicial para que el contenido comience debajo del logo
        yPosition = margins.top + logoHeight + 10; // 10mm de espacio adicional después del logo
      } catch (error) {
        console.log('No se pudo cargar el logo, continuando sin él');
        // Si no hay logo, mantener la posición original
        yPosition = margins.top;
      }
      
      // Encabezado
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`COTIZACIÓN N°: ${quoteCode}`, margins.left, yPosition);
      yPosition += 8;
      
      // Usar la misma fuente y tamaño para todo el documento (excepto tabla)
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`FECHA: ${currentDate}`, margins.left, yPosition);
      yPosition += 6;
      pdf.text(`CLIENTE: ${customerName}`, margins.left, yPosition);
      yPosition += 6;
      pdf.text(`ATENCIÓN: ${attentionContact}`, margins.left, yPosition);
      yPosition += 12;
      
      // Saludo inicial
      pdf.text(`Estimados señores de ${customerName}:`, margins.left, yPosition);
      yPosition += 6;
      
      // Función para agregar texto justificado que ocupa todo el ancho disponible
      const addFullWidthText = (text: string, x: number, y: number, maxWidth: number, preventLineBreak: boolean = false) => {
        // Si se debe evitar saltos de línea, agregar el texto completo sin dividir
        if (preventLineBreak) {
          // Verificar si necesitamos una nueva página antes de agregar el texto
          const pageBreakResult = handlePageBreak(
            pdf,
            y,
            5, // Altura estimada para una línea de texto
            margins,
            { minSpace: 5 }
          );
          y = pageBreakResult.newY;
          
          pdf.text(text, x, y);
          return y + 5; // Espaciado estándar después del texto
        }
        
        // Verificar si necesitamos una nueva página antes de agregar el texto justificado
        const estimatedHeight = text.split('\n').length * 6; // Estimación aproximada
        const pageBreakResult = handlePageBreak(
          pdf,
          y,
          estimatedHeight,
          margins,
          { minSpace: 10 }
        );
        y = pageBreakResult.newY;
        
        // Usar directamente la utilidad de justificación exportada
        return textJustificationUtils.addEnhancedJustifiedText(pdf, text, x, y, maxWidth, {
          fontSize: 9,
          lineHeight: 0.6, // Reducido a 0.6 para menos espaciado
          justifyLastLine: false,
          minSpacing: 0.3 // Reducido a 0.3 para menos espaciado
        });
      };
      
      // Agregar saludo completo ocupando todo el ancho, con justificación completa
      const saludoText = 'Por medio de la presente, nos complace presentarles la siguiente cotización para el suministro de productos de alta calidad:';
      yPosition = addFullWidthText(saludoText, margins.left, yPosition, contentWidth, false);
      yPosition += 8;
      
      // Tabla de productos - ajustar anchos para que quepa todo dentro de los márgenes
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text('Ítem', margins.left, yPosition);
      pdf.text('Producto', margins.left + 12, yPosition);
      pdf.text('Cant.', margins.left + 85, yPosition);
      pdf.text('P. Unit.', margins.left + 105, yPosition);
      pdf.text('Subtotal', margins.left + 130, yPosition);
      yPosition += 6;
      
      // Línea separadora
      pdf.setLineWidth(0.1);
      pdf.line(margins.left, yPosition, 210 - margins.right, yPosition);
      yPosition += 6;
      
      // Detalles de productos
      pdf.setFont('helvetica', 'normal');
      if (quote.quote_details && quote.quote_details.length > 0) {
        quote.quote_details.forEach((item: any, index: number) => {
          // Buscar el nombre del producto por ID
          const product = products.find((p: any) => p.id === item.product_id);
          const productName = product ? product.name : `Producto ${item.product_id}`;
          
          // Verificar si necesitamos una nueva página usando la función unificada
          const pageBreakResult = handlePageBreak(
            pdf,
            yPosition,
            20, // Altura estimada para una fila de producto
            margins,
            {
              minSpace: 20,
              repeatHeader: true,
              headerFunction: (pdf: jsPDF, y: number) => {
                // Repetir encabezado de tabla en nueva página
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.text('Ítem', margins.left, y);
                pdf.text('Producto', margins.left + 12, y);
                pdf.text('Cant.', margins.left + 85, y);
                pdf.text('P. Unit.', margins.left + 105, y);
                pdf.text('Subtotal', margins.left + 130, y);
                y += 6;
                pdf.setLineWidth(0.1);
                pdf.line(margins.left, y, 210 - margins.right, y);
                y += 6;
                pdf.setFont('helvetica', 'normal');
                return y;
              }
            }
          );
          yPosition = pageBreakResult.newY;
          
          pdf.text(`${index + 1}`, margins.left, yPosition);
          
          // Dividir nombres largos de productos en múltiples líneas
          const maxProductNameLength = 50;
          if (productName.length > maxProductNameLength) {
            const words = productName.split(' ');
            let line = '';
            let lineCount = 0;
           
            for (const word of words) {
              if ((line + word).length <= maxProductNameLength) {
                line += (line ? ' ' : '') + word;
              } else {
                if (line) {
                  pdf.text(line, margins.left + 12, yPosition);
                  yPosition += 4;
                  line = word;
                  lineCount++;
                  
                  if (lineCount > 2) { // Máximo 3 líneas por producto
                    pdf.text(line + '...', margins.left + 12, yPosition);
                    yPosition += 4;
                    break;
                  }
                } else {
                  line = word;
                }
              }
            }
            if (line && lineCount <= 2) {
              pdf.text(line, margins.left + 12, yPosition);
              yPosition += 4;
            }
          } else {
            pdf.text(productName, margins.left + 12, yPosition);
            yPosition += 4;
          }
          
          // Ajustar posición para los valores numéricos
          const valueYPosition = yPosition - 4;
          pdf.text(`${item.quantity}`, margins.left + 85, valueYPosition);
          pdf.text(`$${(item.unit_price || 0).toFixed(2)}`, margins.left + 105, valueYPosition);
          pdf.text(`$${(item.total_price || 0).toFixed(2)}`, margins.left + 130, valueYPosition);
          yPosition += 4;
        });
      }
      
      // Línea separadora antes del total
      yPosition += -4;
      pdf.setLineWidth(0.1);
      pdf.line(margins.left, yPosition, 210 - margins.right, yPosition);
      yPosition += 6;
      
      // Total
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('PRECIO TOTAL (USD) SIN IGV:', margins.left + 68, yPosition);
      pdf.text(`$${subtotal.toFixed(2)}`, margins.left + 130, yPosition);
      yPosition += 12;
      
      // Condiciones de la Oferta
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Condiciones de la Oferta', margins.left, yPosition);
      yPosition += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      // Agregar condiciones con viñetas más grandes y sangría
      const bulletSize = 10; // Tamaño de viñeta más grande
      const indentSize = 8; // Sangría para el texto
      
      // Función para agregar texto con viñeta grande y sangría
      const addBulletText = (text: string, y: number) => {
        // Verificar si necesitamos una nueva página antes de agregar la viñeta
        const pageBreakResult = handlePageBreak(
          pdf,
          y,
          8, // Altura estimada para una línea con viñeta
          margins,
          { minSpace: 8 }
        );
        y = pageBreakResult.newY;
        
        // Agregar viñeta más grande
        pdf.setFontSize(bulletSize);
        pdf.setFont('helvetica', 'bold');
        pdf.text('•', margins.left + 2, y); // Viñeta más grande
        
        // Agregar texto con sangría y justificación mejorada
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        
        // Usar directamente la utilidad de justificación para el texto con viñeta
        return textJustificationUtils.addEnhancedJustifiedText(pdf, text, margins.left + indentSize, y, contentWidth - indentSize, {
          fontSize: 9,
          lineHeight: 0.6, // Reducido de 1.0 a 0.6 para menos espaciado
          justifyLastLine: false,
          minSpacing: 0.3 // Reducido de 0.6 a 0.3 (50% menos)
        });
      };
      
      // Función para agregar texto justificado (reutilizable en todo el documento)
      const addJustifiedText = (text: string, x: number, y: number, maxWidth: number) => {
        const words = text.split(' ');
        let line = '';
        let currentY = y;
        let lines = [];
        
        // Primero, dividir el texto en líneas que quepan en el ancho máximo
        for (const word of words) {
          const testLine = line + (line ? ' ' : '') + word;
          const textWidth = pdf.getTextWidth(testLine);
          
          if (textWidth > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = testLine;
          }
        }
        
        // Agregar la última línea
        if (line) {
          lines.push(line);
        }
        
        // Ahora agregar cada línea con justificación (excepto la última)
        for (let i = 0; i < lines.length; i++) {
          const lineText = lines[i];
          const isLastLine = i === lines.length - 1;
          
          // Si es la última línea o la línea tiene una sola palabra, no justificar
          if (isLastLine || lineText.split(' ').length <= 1) {
            pdf.text(lineText, x, currentY);
          } else {
            // Justificar la línea: distribuir el espacio entre palabras
            const wordsInLine = lineText.split(' ');
            const totalWords = wordsInLine.length;
            
            // Identificar palabras que terminan en puntuación para no agregar espacio extra después
            const punctuationWords = wordsInLine.map((word, index) => {
              const endsWithPunctuation = /[.,;:!?]$/.test(word);
              return { word, endsWithPunctuation, index };
            });
            
            // Calcular el ancho total del texto sin espacios
            let textWidthWithoutSpaces = 0;
            wordsInLine.forEach(word => {
              textWidthWithoutSpaces += pdf.getTextWidth(word);
            });
            
            // Calcular el espacio total disponible para distribuir
            const totalSpaceAvailable = maxWidth - textWidthWithoutSpaces;
            
            // Ajustar el espacio entre palabras, dando menos espacio a las que terminan en puntuación
            let spaceBetweenWords = totalSpaceAvailable / (totalWords - 1);
            const punctuationAdjustment = 0.3; // Reducir espacio en 30% para palabras con puntuación
            
            // Dibujar cada palabra con el espacio calculado
            let currentX = x;
            wordsInLine.forEach((word, index) => {
              pdf.text(word, currentX, currentY);
              currentX += pdf.getTextWidth(word);
              
              // Agregar espacio entre palabras (excepto después de la última)
              if (index < totalWords - 1) {
                const endsWithPunctuation = /[.,;:!?]$/.test(word);
                const adjustedSpace = endsWithPunctuation ?
                  spaceBetweenWords * (1 - punctuationAdjustment) :
                  spaceBetweenWords;
                currentX += adjustedSpace;
              }
            });
          }
          
          currentY += 2; // Reducido de 5 a 2 para menos espaciado entre líneas
        }
        
        return currentY;
      };
      
      // Función para justificar texto con control preciso de espaciado (para tablas y áreas específicas)
      const addPreciseJustifiedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 9) => {
        // Guardar el tamaño de fuente actual
        const currentFontSize = pdf.getFontSize();
        pdf.setFontSize(fontSize);
        
        const words = text.split(' ');
        let line = '';
        let currentY = y;
        let lines = [];
        
        // Dividir el texto en líneas que quepan en el ancho máximo
        for (const word of words) {
          const testLine = line + (line ? ' ' : '') + word;
          const textWidth = pdf.getTextWidth(testLine);
          
          if (textWidth > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = testLine;
          }
        }
        
        if (line) {
          lines.push(line);
        }
        
        // Procesar cada línea con justificación precisa
        for (let i = 0; i < lines.length; i++) {
          const lineText = lines[i];
          const isLastLine = i === lines.length - 1;
          
          if (isLastLine || lineText.split(' ').length <= 1) {
            pdf.text(lineText, x, currentY);
          } else {
            const wordsInLine = lineText.split(' ');
            const totalWords = wordsInLine.length;
            
            // Calcular anchos precisos
            let textWidthWithoutSpaces = 0;
            const wordWidths: number[] = [];
            
            wordsInLine.forEach(word => {
              const width = pdf.getTextWidth(word);
              wordWidths.push(width);
              textWidthWithoutSpaces += width;
            });
            
            const totalSpaceAvailable = maxWidth - textWidthWithoutSpaces;
            let spaceBetweenWords = totalSpaceAvailable / (totalWords - 1);
            
            // Ajuste fino para caracteres especiales y acentos
            const hasSpecialChars = /[áéíóúÁÉÍÓÚñÑüÜ]/.test(lineText);
            if (hasSpecialChars) {
              spaceBetweenWords *= 0.98; // Reducir ligeramente para compensar caracteres anchos
            }
            
            // Dibujar con posicionamiento preciso
            let currentX = x;
            wordsInLine.forEach((word, index) => {
              pdf.text(word, currentX, currentY);
              currentX += wordWidths[index];
              
              if (index < totalWords - 1) {
                const endsWithPunctuation = /[.,;:!?]$/.test(word);
                const adjustedSpace = endsWithPunctuation ?
                  spaceBetweenWords * 0.7 :
                  spaceBetweenWords;
                currentX += adjustedSpace;
              }
            });
          }
          
          currentY += fontSize * 0.4; // Reducido de 0.5 a 0.4 para menos espaciado
        }
        
        // Restaurar el tamaño de fuente original
        pdf.setFontSize(currentFontSize);
        return currentY;
      };
      
      
      // Función para manejar saltos de página con texto justificado
      const addJustifiedTextWithPageBreak = (
        text: string,
        x: number,
        y: number,
        maxWidth: number,
        maxY: number = 277, // Límite inferior de la página
        options: {
          fontSize?: number;
          lineHeight?: number;
          justifyLastLine?: boolean;
          minSpacing?: number;
        } = {}
      ): { finalY: number; pagesAdded: number } => {
        let pagesAdded = 0;
        let currentY = y;
        
        // Dividir el texto en párrafos (separados por dobles saltos de línea)
        const paragraphs = text.split('\n\n');
        
        for (const paragraph of paragraphs) {
          // Verificar si necesitamos una nueva página antes de este párrafo
          if (currentY > maxY - 20) { // Dejar espacio para al menos una línea
            pdf.addPage();
            currentY = margins.top;
            pagesAdded++;
            
            // Opcional: repetir encabezado en nueva página
            // (se puede personalizar según necesidades)
          }
          
          // Agregar el párrafo justificado usando directamente la utilidad
          currentY = textJustificationUtils.addEnhancedJustifiedText(pdf, paragraph, x, currentY, maxWidth, options);
          currentY += 0.5; // Reducido de 1 a 0.5 para menos espaciado entre párrafos
        }
        
        return { finalY: currentY, pagesAdded };
      };
      
      // Función para justificar texto en columnas (para diseños más complejos)
      const addJustifiedTextInColumns = (
        text: string,
        x: number,
        y: number,
        columnWidth: number,
        columnGap: number = 10,
        numColumns: number = 2,
        options: {
          fontSize?: number;
          lineHeight?: number;
          justifyLastLine?: boolean;
        } = {}
      ): { finalY: number; usedColumns: number } => {
        const words = text.split(' ');
        let currentX = x;
        let currentY = y;
        let currentColumn = 0;
        let currentLine = '';
        
        const {
          fontSize = 9,
          lineHeight = 1.2,
          justifyLastLine = false
        } = options;
        
        // Guardar configuración actual
        const currentFontSize = pdf.getFontSize();
        pdf.setFontSize(fontSize);
        
        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const textWidth = pdf.getTextWidth(testLine);
          
          if (textWidth > columnWidth && currentLine) {
            // Dibujar la línea actual justificada
            const wordsInLine = currentLine.split(' ');
            const shouldJustify = wordsInLine.length > 1;
            
            if (shouldJustify) {
              const { spaceBetweenWords, wordWidths } = textJustificationUtils.calculateOptimalSpacing(wordsInLine, columnWidth, pdf);
              let lineX = currentX;
              
              wordsInLine.forEach((w, idx) => {
                pdf.text(w, lineX, currentY);
                lineX += wordWidths[idx];
                if (idx < wordsInLine.length - 1) {
                  lineX += spaceBetweenWords;
                }
              });
            } else {
              pdf.text(currentLine, currentX, currentY);
            }
            
            currentY += fontSize * 0.6; // Reducido de 0.8 a 0.6 para menos espaciado
            currentLine = word;
            
            // Verificar si necesitamos cambiar de columna
            if (currentY > 277) {
              currentColumn++;
              if (currentColumn < numColumns) {
                currentX += columnWidth + columnGap;
                currentY = y;
              } else {
                // No hay más columnas, terminar
                break;
              }
            }
          } else {
            currentLine = testLine;
          }
        }
        
        // Dibujar la última línea
        if (currentLine && currentColumn < numColumns) {
          pdf.text(currentLine, currentX, currentY);
        }
        
        // Restaurar configuración
        pdf.setFontSize(currentFontSize);
        
        return { finalY: currentY, usedColumns: Math.min(currentColumn + 1, numColumns) };
      };
      
      yPosition = addBulletText(`Validez: Oferta válida hasta el ${validityDateStr}.`, yPosition);
      yPosition = addBulletText(`Moneda: ${currency === 'USD' ? 'Dólares Americanos (USD)' : currency === 'PEN' ? 'Soles (PEN)' : 'Euros (EUR)'}.`, yPosition);
      yPosition = addBulletText(`Pago: ${paymentTerms}.`, yPosition);
      yPosition = addBulletText(`Entrega: ${deliveryTerms}.`, yPosition);
      
      // Texto de observaciones con viñetas mejoradas y sangría
      // Primero agregar la observación fija con el mismo formato
      yPosition = addBulletText(`Observaciones: ${fixedObservation}`, yPosition);
      
      // Luego agregar condiciones adicionales si existen y están marcadas para PDF
      if (additionalConditions && additionalConditions.length > 0) {
        const pdfConditions = additionalConditions.filter(condition => condition.includeInPDF);
        
        if (pdfConditions.length > 0) {
          pdfConditions.forEach((condition, index) => {
            const conditionName = condition.name || `Condición ${index + 1}`;
            const conditionText = condition.content || '';
            // Cada condición debe tener su propia viñeta
            yPosition = addBulletText(`${conditionName}: ${conditionText}`, yPosition);
          });
        }
      }
      
      // Mantener compatibilidad con observaciones adicionales antiguas (filtradas)
      if (filteredObservations) {
        // Dividir las observaciones adicionales por el separador | que se usa en el servicio
        const additionalLines = filteredObservations.split(' | ').filter(line => line.trim());
        additionalLines.forEach(line => {
          // Eliminar las banderas [PDF:YES/NO] del texto antes de mostrarlo en el PDF
          let cleanLine = line.trim();
          // Solo eliminar la bandera y un espacio antes si existe, pero preservar espacios entre condiciones
          cleanLine = cleanLine.replace(/\s*\[PDF:(YES|NO)\]/g, '');
          
          // EVITAR DUPLICACIÓN: No agregar la línea si es igual a la observación fija que ya se agregó
          // Comparamos el texto limpio con el contenido de la observación fija (sin el prefijo "Observaciones: ")
          if (cleanLine === fixedObservation) {
            console.log('🚫 Evitando duplicación de observación fija:', cleanLine);
            return; // Saltar esta línea para evitar duplicación
          }
          
          // Verificar si la línea ya tiene el formato "Nombre: Contenido"
          if (cleanLine.includes(':')) {
            // Si ya tiene el formato nombre:contenido, usarlo directamente
            yPosition = addBulletText(cleanLine, yPosition);
          } else {
            // Si no, agregarlo como observación adicional simple
            yPosition = addBulletText(cleanLine, yPosition);
          }
        });
      }
      yPosition += 4;
      
      // Información para generar orden de compra - línea por línea
      const orderInfoLines = [
        { text: 'Generar la Orden de Compra a:', fontSize: 9, fontStyle: 'bold' },
        { text: 'Empresa: ATOMIC CORPORATION SRL', fontSize: 9, fontStyle: 'normal' },
        { text: 'RUC: 20606452111', fontSize: 9, fontStyle: 'normal' },
        { text: 'Correo: ventas@atomcorporations.com', fontSize: 9, fontStyle: 'normal' }
      ];
      
      yPosition = addTextLineByLine(pdf, orderInfoLines, margins.left, yPosition, margins, 5);
      yPosition += 4; // Espacio adicional después de la información de contacto
      
      // Cierre con texto que ocupa todo el ancho, con justificación completa
      const cierreText = 'Agradecemos su atención y quedamos a su disposición para cualquier aclaración o para especificar las cantidades requeridas y proceder con la Orden de Compra.';
      yPosition = addFullWidthText(cierreText, margins.left, yPosition, contentWidth, false);
      yPosition += 8;
      
      // Firma - línea por línea
      const signatureLines = [
        { text: 'Atentamente,', fontSize: 9, fontStyle: 'normal' }
      ];
      
      yPosition = addTextLineByLine(pdf, signatureLines, margins.left, yPosition, margins, 8);
      yPosition += 8; // Espacio antes de la línea de firma
      
      // Verificar espacio para la línea de firma
      const signatureResult = handlePageBreak(
        pdf,
        yPosition,
        10, // Altura estimada para la sección de firma
        margins,
        { minSpace: 15 }
      );
      yPosition = signatureResult.newY;
      
      // Línea de firma
      pdf.line(margins.left, yPosition, margins.left + 60, yPosition);
      yPosition += 5;
      
      // Información de la firma - línea por línea
      const signatureInfoLines = [
        { text: 'Luz Espinoza Rengifo', fontSize: 9, fontStyle: 'bold' },
        { text: 'Asesora de Ventas', fontSize: 9, fontStyle: 'normal' },
        { text: 'Atomic Corporation SRL', fontSize: 9, fontStyle: 'normal' }
      ];
      
      yPosition = addTextLineByLine(pdf, signatureInfoLines, margins.left, yPosition, margins, 5);
      
      // Agregar pie de página en todas las páginas
      addFooter(pdf, margins);
      
      // Generar base64
      const base64 = pdf.output('datauristring').split(',')[1];
      const size = Math.round(base64.length * 0.75 / 1024);
      console.log(`📊 Tamaño del PDF generado: ${size} KB`);
      
      return base64;
    } catch (error) {
      console.error('Error al generar PDF como base64:', error);
      return null;
    }
  },
  
  // Función de ejemplo para generar un PDF con texto justificado (reutilizable)
  generateJustifiedTextPDF: async (
    title: string,
    content: string[],
    fileName: string = 'documento-justificado.pdf'
  ): Promise<void> => {
    try {
      // Crear PDF
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });
      
      // Definir márgenes
      const margins = {
        left: 20,
        top: 20,
        right: 20,
        bottom: 20
      };
      
      const contentWidth = 210 - margins.left - margins.right;
      let yPosition = margins.top;
      
      // Configurar fuente
      pdf.setFont('helvetica');
      pdf.setFontSize(12);
      
      // Agregar título
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, margins.left, yPosition);
      yPosition += 15;
      
      // Agregar contenido justificado
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      
      content.forEach((paragraph, index) => {
        // Usar la utilidad de justificación exportada
        yPosition = textJustificationUtils.addEnhancedJustifiedText(
          pdf,
          paragraph,
          margins.left,
          yPosition,
          contentWidth,
          {
            fontSize: 10,
            lineHeight: 1.5,
            justifyLastLine: false,
            minSpacing: 1.0
          }
        );
        
        // Agregar espacio entre párrafos
        yPosition += 8;
        
        // Verificar si necesitamos una nueva página
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = margins.top;
        }
      });
      
      // Agregar pie de página en todas las páginas
      addFooter(pdf, margins);
      
      // Descargar el PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error al generar PDF con texto justificado:', error);
      throw new Error('No se pudo generar el PDF con texto justificado');
    }
  }
};